import { pxToEm } from 'util/pxToEm';
import React, { ReactElement } from 'react';
import {
  Card,
  Typography,
  TextField,
  InputAdornment,
  useTheme,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useSchoolsApi } from 'services/api';
import { Button } from 'components/ui';
import {
  colorYellowHover,
  colorDisabledBg,
  colorDisabledText,
} from 'customColors';

export interface CalendarSettingsProps {
  schoolId: string;
  minimumEventSubmissionDays: number;
  onUpdate: (value: number) => void;
}

export const CalendarSettings = ({
  schoolId,
  minimumEventSubmissionDays: initialValue,
  onUpdate,
}: CalendarSettingsProps): ReactElement => {
  const theme = useTheme();
  const { enqueueSnackbar } = useSnackbar();
  const schoolsApi = useSchoolsApi();
  const [minimumEventSubmissionDays, setMinimumEventSubmissionDays] =
    React.useState(initialValue);
  const [originalMinimumDays, setOriginalMinimumDays] =
    React.useState(initialValue);
  const [isSavingCalendarSettings, setIsSavingCalendarSettings] =
    React.useState(false);

  React.useEffect(() => {
    setMinimumEventSubmissionDays(initialValue);
    setOriginalMinimumDays(initialValue);
  }, [initialValue]);

  const hasCalendarSettingsChanges = React.useMemo(() => {
    return minimumEventSubmissionDays !== originalMinimumDays;
  }, [minimumEventSubmissionDays, originalMinimumDays]);

  const handleSaveCalendarSettings = async () => {
    if (!schoolId) return;
    setIsSavingCalendarSettings(true);
    try {
      const result = await schoolsApi.saveSchool(schoolId, {
        minimumEventSubmissionDays: Number(minimumEventSubmissionDays) || 0,
      });

      if (result.success) {
        enqueueSnackbar('Calendar Settings updated.', {
          variant: 'success',
        });
        setOriginalMinimumDays(minimumEventSubmissionDays);
        onUpdate(minimumEventSubmissionDays);
      } else {
        enqueueSnackbar(result.error.message, { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar('Failed to update calendar settings', {
        variant: 'error',
      });
    } finally {
      setIsSavingCalendarSettings(false);
    }
  };

  return (
    <Card
      sx={{
        border: `1px solid ${theme.palette.neutral.dark}`,
        bgcolor: '#fff',
        borderRadius: '8px',
        boxShadow: 'none',
        p: '24px',
      }}
    >
      <Typography
        fontSize={{ xs: pxToEm(14), sm: pxToEm(15) }}
        fontWeight={400}
        color={theme.palette.text.secondary}
        mb={24}
      >
        Set how many days in advance an event must be submitted before it can
        occur.
      </Typography>

      <Typography
        fontSize={{ xs: pxToEm(16), sm: pxToEm(18) }}
        fontWeight={500}
        color={theme.palette.text.primary}
        mb={16}
      >
        Minimum calendar days before an event
      </Typography>

      <TextField
        type="number"
        value={minimumEventSubmissionDays}
        onChange={e => {
          const value = parseInt(e.target.value, 10) || 0;
          setMinimumEventSubmissionDays(Math.max(0, Math.min(60, value)));
        }}
        placeholder="0"
        inputProps={{
          min: 0,
          max: 365,
          step: 1,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Typography
                sx={{
                  color: theme.palette.text.secondary,
                  fontSize: pxToEm(14),
                }}
              >
                days
              </Typography>
            </InputAdornment>
          ),
        }}
        sx={{
          maxWidth: '150px',
          minWidth: '150px',
          mb: 32,
          '& .MuiOutlinedInput-root': {
            borderRadius: '8px',
            '& fieldset': {
              borderColor: theme.palette.neutral.dark,
            },
            '&:hover fieldset': {
              borderColor: theme.palette.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: theme.palette.primary.main,
            },
          },
        }}
      />

      <Button
        variant="vibrant"
        onClick={handleSaveCalendarSettings}
        disabled={isSavingCalendarSettings || !hasCalendarSettingsChanges}
        sx={{
          display: 'flex',
          width: 'auto',
          height: '47px',
          minHeight: '47px',
          maxHeight: '47px',
          padding: '16px',
          justifyContent: 'center',
          alignItems: 'center',
          gap: '8px',
          bgcolor: theme.palette.vibrant.primary,
          color: '#000',
          fontWeight: 500,
          fontSize: pxToEm(14),
          textTransform: 'none',
          borderRadius: '8px',
          '&:hover': {
            bgcolor: colorYellowHover,
          },
          '&:disabled': {
            bgcolor: colorDisabledBg,
            color: colorDisabledText,
          },
        }}
      >
        {isSavingCalendarSettings ? 'Saving...' : 'Save Calendar Settings'}
      </Button>
    </Card>
  );
};

